import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { siteLocales } from '@yola/ws-sdk';
import getLanguages from 'src/js/modules/website-settings/helpers/get-languages';
import getCountries from 'src/js/modules/website-settings/helpers/get-countries';
import getLanguageData from 'src/js/modules/website-settings/helpers/get-language-data';
import fieldNames from '../constants/field-names';

const { Stack, SearchableSelectField, LanguageSelect } = designSystem;
const { useWatch } = reactHookForm;

function PageField({ control, options, menuPlacement, setFormValue, onChange }) {
  const locales = siteLocales.accessors.getSiteLocales();
  const currentLocale = siteLocales.accessors.getCurrentLocale();

  const localesOptions = useMemo(() => {
    const langMap = getLanguages();
    const countriesMap = getCountries();
    return locales.map((local) => {
      const {
        name,
        country: { iconComponent },
      } = getLanguageData(local, langMap, countriesMap);
      return {
        value: local,
        label: name,
        iconComponent,
      };
    });
  }, [locales]);

  const reference = useWatch({ control, name: fieldNames.PAGE_REFERENCE });

  const getActiveLocale = useMemo(() => {
    const activeOption = options.find(({ value }) => value === reference);
    return activeOption?.locale || currentLocale;
  }, [options, reference, currentLocale]);

  const [activeLocale, setActiveLocale] = useState(getActiveLocale);

  const pagesOptions = useMemo(
    () =>
      localesOptions.length > 1 ? options.filter(({ locale }) => locale === activeLocale) : options,
    [localesOptions, options, activeLocale]
  );

  const defaultLanguageOption = useMemo(
    () => localesOptions.find(({ value }) => value === activeLocale),
    [localesOptions, activeLocale]
  );

  const handleChange = useCallback(
    (value) => {
      onChange({
        fieldName: fieldNames.PAGE_REFERENCE,
        fieldValue: value,
      });
    },
    [onChange]
  );

  const handleSwitchLanguage = useCallback(
    ({ value: langValue }) => {
      const localePages = options.filter(({ locale }) => locale === langValue);
      const firstPageValue = localePages[0]?.value;

      setFormValue(fieldNames.PAGE_REFERENCE, firstPageValue, { shouldDirty: true });
      handleChange(firstPageValue);
      setActiveLocale(langValue);
    },
    [options, setFormValue, handleChange]
  );

  return (
    <Stack gap="spacing-3-xs">
      {localesOptions.length > 1 && (
        <LanguageSelect
          options={localesOptions}
          defaultValue={defaultLanguageOption}
          menuPlacement={menuPlacement}
          onChange={handleSwitchLanguage}
        />
      )}

      <SearchableSelectField
        key={activeLocale}
        name={fieldNames.PAGE_REFERENCE}
        control={control}
        options={pagesOptions}
        menuPlacement={menuPlacement}
        onChange={handleChange}
      />
    </Stack>
  );
}

PageField.propTypes = {
  control: PropTypes.shape({}).isRequired,
  options: PropTypes.array.isRequired,
  menuPlacement: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  setFormValue: PropTypes.func.isRequired,
};

export default PageField;
